import _ from 'lodash';
import { WithData } from 'vue-data-component';

export default {
    components: { WithData },
    props: {
        url: {
            type: String,
            required: false,
        },
        columns: {
            type: [Array, Object],
            default: () => [],
        },
        tag: {
            type: String,
            default: 'div',
        },
        history: {
            type: Boolean,
            default: false,
        },
        pagination: {
            type: Boolean,
            default: false,
        },
        perPage: {
            type: Array,
            default: () => [10, 25, 50],
        },
        searchPlaceholder: {
            type: String,
            default: 'Search',
        },
        selectable: {
            type: [Boolean, Object],
            default: false,
        },
        tableClass: {
            type: String,
            default: 'table',
        },
        id: {
            type: String,
            default: 'table',
        },
        data: {
            type: [Object, Array, Boolean],
            default: false,
        },
        query: {
            type: Object,
            default: () => ({}),
        },
        value: {
            default: () => [],
        },
        hideEmpty: {
            type: Boolean,
            default: false,
        },
        transformer: {
            type: Function,
            default: null,
        },
        itemTransformer: {
            type: Function,
            default: null,
        },
        debounce: {
            type: Number,
            default: 200,
        },
    },
    data() {
        return {
            busy: false,
            selectableConfig: false,
            initialLoad: true,
            selectAll: false,
            tableColumns: {},
            defaultQuery: {
                page: 1,
            },
            selection: [],
        };
    },
    computed: {
        proxy: {
            get() {
                return this.query;
            },
            set(newVal) {
                this.$emit('update:query', newVal);
            },
        },
        computedSearch: {
            get() {
                return _.get(this.query, 'filter.search');
            },
            set(newVal) {
                _.set(this.query, 'filter.search', newVal);
            },
        },
        rows() {
            return _.get(this.$refs.withData, 'result.data', []);
        },
    },
    created() {
        this.selection = _.isArray(this.value) ? this.value : [];
        Bus.$on(this.id + 'TableRefresh', this.refresh);
    },
    beforeDestroy() {
        Bus.$off(this.id + 'TableRefresh', this.refresh);
    },
    methods: {
        _get: _.get,
        _defaultTo: _.defaultTo,
        defaultTransformer(response) {
            if (this.data) {
                return {
                    meta: {},
                    data: response,
                    total: _.toInteger(Array.isArray(response) ? response.length : Object.keys(response).length),
                    perPage: _.toInteger(_.get(this, 'proxy.perPage', 15)),
                };
            }

            return {
                meta: _.get(response, 'data.meta', _.get(response, 'meta', null)),
                data: _.get(response, 'data.data', _.get(response, 'data', null)),
                total: _.toInteger(
                    _.get(response, 'data.meta.pagination.total', _.get(response, 'data.meta.total', _.get(response, 'data.total', 0))),
                ),
                perPage: _.toInteger(
                    _.get(response, 'data.meta.pagination.per_page', _.get(response, 'data.meta.per_page', _.get(response, 'data.per_page', 15))),
                ),
            };
        },
        getTableData({ queryString }) {
            const self = this;
            return new Promise((resolve) => {
                self.$messenger.loading();
                if (self.data) {
                    resolve(self.resolveData(self.data));
                    self.$messenger.loaded();
                } else {
                    axios
                        .get(`${self.url}?${queryString}`)
                        .then((response) => {
                            resolve(self.resolveData(response));
                        })
                        .finally(() => {
                            self.$messenger.loaded();
                        });
                }
            });
        },
        resolveData(data) {
            let resolvedData = this.transformer ? this.transformer(data) : this.defaultTransformer(data);
            resolvedData.data = this.itemTransformer ? _.map(resolvedData.data, this.itemTransformer) : resolvedData.data;
            this.onLoad(resolvedData);

            return resolvedData;
        },
        onLoad(resolvedData) {
            this.$emit('loaded', resolvedData);
            Bus.$emit(this.id + 'TableLoaded', resolvedData);

            if (this.initialLoad) {
                this.$emit('initialLoad', resolvedData);
                this.initialLoad = false;
            }

            // Selectable
            if (this.selectableConfig) {
                if (!this.selectableConfig.persist) {
                    Vue.set(
                        this,
                        'selection',
                        _.intersection(
                            this.selection,
                            _.map(resolvedData.data, (item) => _.get(item, this.selectableConfig.attribute)),
                        ),
                    );
                }
            }
        },
        refresh() {
            this.$refs.withData.forceUpdate();
        },
        toggleAll(checked) {
            this.selection = checked ? _.map(this.rows, (row) => _.get(row, this.selectableConfig.attribute)) : [];
        },
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.refresh();
            },
        },
        selection: {
            deep: true,
            handler(newVal) {
                if (this.selectableConfig !== false) {
                    this.$emit('input', newVal);
                }
            },
        },
        selectable: {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.selectableConfig = {
                        attribute: _.get(newVal, 'attribute', 'id'),
                        persist: _.get(newVal, 'persist', true),
                        objects: _.get(newVal, 'objects', false),
                        default: _.get(newVal, 'default', false),
                    };
                }
            },
        },
        columns: {
            deep: true,
            immediate: true,
            handler(newVal) {
                this.tableColumns = _.transform(
                    newVal,
                    (accumulator, value, key) => {
                        if (_.isString(value)) {
                            accumulator[value] = { label: value, key: value };
                        } else if (_.isObject(value)) {
                            key = value.key ? value.key : key;
                            accumulator[key] = _.merge({ key: key, label: value.label }, value);
                        } else {
                            this.tableColumns = newVal;
                        }

                        return accumulator;
                    },
                    {},
                );
            },
        },
    },
};
